<template>
  <div
    :class="
      twMerge(
        'relative mx-auto max-w-[1920px] lg:grid lg:grid-cols-2 lg:gap-5 lg:last:*:rounded-2xl',
        //@ts-ignore
        $attrs.class,
      )
    "
  >
    <div
      v-if="image"
      class="relative z-10 aspect-[375/230] w-full overflow-hidden rounded-t-2xl md:aspect-auto md:h-[295px] lg:aspect-[912/752] lg:h-full lg:rounded-2xl"
    >
      <CloudinaryVideo
        v-if="isVideo"
        :url="image"
        :alt="imageAlt"
        :critical="cta || criticalImage"
      />
      <Image
        v-else
        :url="image"
        :alt="imageAlt"
        image-class="h-full w-full object-cover"
        :width="1200"
        :fetchpriority="cta || criticalImage ? 'high' : 'auto'"
        :loading="cta || criticalImage ? 'eager' : 'lazy'"
      />
    </div>
    <div :class="twMerge(contentVariants({ theme }), reverse && contentReverseVariants({ theme }))">
      <div class="mb-2 w-full max-w-[600px] px-6 pb-8 pt-6 lg:mb-0 lg:px-8">
        <template v-if="cta">
          <SubTitle v-if="subtitle" :class="subTitleVariants({ theme })">
            {{ subtitle }}
          </SubTitle>
          <Title v-if="title" :class="titleCtaVariants({ theme })" :level="1">
            {{ title }}
          </Title>
          <div
            :class="descriptionCtaVariants({ theme })"
            v-html="isMarkdown(description) ? markdownToHtml(description) : description"
          />
          <Button
            v-if="siteOrg !== 'raddadjuren'"
            :title="buttonLabel || $t('button.next')"
            color="pink"
            class="mt-12 w-full md:mt-5 lg:mt-12 lg:w-96"
            :arrow-right="true"
            @click="$emit('onNext')"
          />

          <ButtonAct
            v-if="siteOrg === 'raddadjuren'"
            :title="buttonLabel || $t('button.give-membership')"
            :theme="theme"
            :isCta="true"
            :variant="'primary'"
            :iconType="'arrowRight'"
            @click="$emit('onNext')"
          />
        </template>
        <template v-else>
          <SubTitle v-if="subtitle" :class="subTitleVariants({ theme })">
            {{ subtitle }}
          </SubTitle>
          <Title v-if="title" :class="titleVariants({ theme })" :level="2">
            {{ title }}
          </Title>
          <div
            v-if="preamble"
            :class="preambleVariants({ theme })"
            v-html="isMarkdown(preamble) ? markdownToHtml(preamble) : preamble"
          />
          <div :class="descriptionVariants({ theme })">
            <div v-html="descriptionHtml"></div>
          </div>

          <Button
            v-if="siteOrg !== 'raddadjuren' && isThirdBlock"
            :title="buttonLabel || 'Läs mer'"
            color="blue"
            class="my-8 w-full md:w-80 lg:my-12"
            :arrow-right="true"
            @click="$emit('onNext')"
          />

          <ButtonAct
            v-if="siteOrg === 'raddadjuren' && buttonLabel"
            :title="buttonLabel"
            :theme="theme"
            :isCta="true"
            :variant="'secondary'"
            :iconType="'arrowRight'"
            @click="$emit('onNext')"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CloudinaryVideo from '../atoms/CloudinaryVideo.vue'
import Image from '../atoms/Image.vue'
import { isCloudinaryVideo } from '../../libs/cloudinaryVideo'
import { Title, SubTitle, THEMES } from 'refresh-ui'
import { twMerge } from 'tailwind-merge'
import { isMarkdown, markdownToHtml } from '../../libs/stringTools'
import { useRichText } from '../../composables/useRichText'

import { cva } from 'class-variance-authority'
import { computed } from 'vue'
import ButtonAct from '../atoms/ButtonAct.vue'
import type { SiteOrg } from '../../types/env'
import Button from 'refresh-ui/src/components/atoms/Button.vue'

const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    preamble?: string
    /**
     * Cloudinary media URL. Despite the name this takes a **video as well as an image**: the CMS
     * field accepts either and renaming it would need a content migration. A clip is spotted by
     * its delivery URL and plays silent and looping; anything else renders as the still it was.
     */
    image?: string
    description?: any
    /** Sanitized HTML that stands in for `description` below the title (a non-CTA block). */
    bodyHtml?: string
    imageAlt?: string
    cta?: boolean
    reverse?: boolean
    theme?: THEMES
    buttonLabel?: string
    siteOrg?: SiteOrg
    isThirdBlock?: boolean
    criticalImage?: boolean
  }>(),
  {
    preamble: '',
    description: '',
    cta: false,
    reverse: false,
    theme: THEMES.RDK,
    buttonLabel: '',
    isThirdBlock: false,
    criticalImage: false,
  },
)

defineEmits(['onNext'])

const isVideo = computed(() => isCloudinaryVideo(props.image))

const descriptionHtml = computed(() => props.bodyHtml || useRichText(props.description))

const contentVariants = cva(
  'relative z-10 -mt-5 lg:mt-4 flex w-full flex-col items-center justify-center lg:rounded-2xl lg:mt-0 lg:-mb-0 lg:aspect-[912/752]',
  {
    variants: {
      theme: {
        [THEMES.DEFAULT]: '',
        [THEMES.RDK]: 'bg-rdk',
        [THEMES.DJURENS_RATT]: 'bg-blue-lighter lg:py-8',
      },
    },
    defaultVariants: {
      theme: THEMES.DEFAULT,
    },
  },
)

const contentReverseVariants = cva('lg:order-first', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]: ' bg-[#F2FBFD]',
      [THEMES.DJURENS_RATT]: ' bg-white',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const subTitleVariants = cva(
  'inline-block text-base font-normal leading-tight md:text-lg md:leading-none',
  {
    variants: {
      theme: {
        [THEMES.DEFAULT]: '',
        [THEMES.RDK]: 'font-avenir text-purple-act',
        [THEMES.DJURENS_RATT]: 'font-serif text-blue',
      },
    },
    defaultVariants: {
      theme: THEMES.DEFAULT,
    },
  },
)

const titleCtaVariants = cva('mb-[18px] lg:mb-6 ', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]:
        'font-normal font-headline text-purple-act-dark text-h2 lg:text-h2 leading-[50px] lg:leading-[72px]',
      [THEMES.DJURENS_RATT]:
        'font-serif text-black text-3xl leading-[34px] md:text-4xl md:leading-none lg:!text-4xl',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const descriptionCtaVariants = cva(
  'prose max-w-none text-[18px] font-bold leading-[21px] md:prose-lg prose-p:text-lg prose-a:no-underline md:leading-[26px] [&_p]:font-bold ',
  {
    variants: {
      theme: {
        [THEMES.DEFAULT]: '',
        [THEMES.RDK]:
          'font-avenir font-normal text-purple-act-dark prose-a:text-purple-act-dark hover:prose-a:text-purple-act-dark [&_p]:text-purple-act-dark text-body leading-[26px]',
        [THEMES.DJURENS_RATT]:
          '!text-blue prose-a:text-blue hover:prose-a:text-blue [&_p]:text-blue font-sans',
      },
    },
    defaultVariants: {
      theme: THEMES.DEFAULT,
    },
  },
)

const titleVariants = cva('mb-[18px] ', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]:
        'font-headline font-normal text-purple-act-dark leading-snug text-h2 lg:text-h2',
      [THEMES.DJURENS_RATT]: 'font-serif text-black !text-3xl leading-[34px] md:leading-[48px]',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const descriptionVariants = cva(
  "mt-5 space-y-4 text-base leading-tight [&_li]:relative [&_li]:before:absolute [&_li]:before:left-[-40px] [&_li]:before:top-0 [&_li]:before:block [&_li]:before:h-[22px] [&_li]:before:w-[22px] [&_li]:before:content-[''] [&_ul]:space-y-4 [&_ul]:pl-10 [&_li]:before:bg-contain [&_li]:before:bg-no-repeat",
  {
    variants: {
      theme: {
        [THEMES.DEFAULT]: '',
        [THEMES.RDK]:
          "[&_ul]:mt-5 text-purple-act-dark font-avenir text-body [&_li]:before:bg-[url('@/assets/icons/list-icon-rdk.svg')]",
        [THEMES.DJURENS_RATT]:
          "[&_ul]:mt-5 font-sans text-black [&_li]:before:bg-[url('/icons/list-icon.svg')]",
      },
    },
    defaultVariants: {
      theme: THEMES.DEFAULT,
    },
  },
)

const preambleVariants = cva(
  'prose max-w-none  text-[18px] font-bold leading-[21px]  md:prose-lg prose-p:text-lg  prose-a:no-underline  md:leading-[26px] [&_p]:font-bold ',
  {
    variants: {
      theme: {
        [THEMES.DEFAULT]: '',
        [THEMES.RDK]:
          '!text-purple-act-dark prose-a:text-purple-act-dark font-avenir hover:prose-a:text-purple-act-dark [&_p]:!text-purple-act-dark text-preamble leading-[24px]',
        [THEMES.DJURENS_RATT]:
          '!text-blue prose-a:text-blue font-sans hover:prose-a:text-blue [&_p]:!text-blue',
      },
    },
    defaultVariants: {
      theme: THEMES.DEFAULT,
    },
  },
)
</script>
